import { useApolloClient } from '@apollo/client';
import Grid from '@mui/material/Grid';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import { appRoutes } from 'app/app-routes';
import ConfirmDialog from 'common/components/ConfirmDialog';
import { ListingUpdate_Input, useListingsAddMutation, useListingsUpdateMutation } from 'graphql/generated';
import _ from 'lodash';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { cacheActions, cacheSelectors } from 'redux/cache.slice';
import { newListingActions, newListingSelectors } from 'redux/new-listing.slice';
import { RootState } from 'redux/root-reducer';
import WsFlexbox from 'stories/layout/WsFlexbox';
import WsGridContainer from 'stories/layout/WsGridContainer';
import WsInfoFrame from 'stories/layout/WsInfoFrame';

import ListingCarInfo from './ListingCarInfo';
import ListingFeatures from './ListingFeatures';
import ListingModelInfo from './ListingModelInfo';
import ListingPhotos from './ListingPhotos';
import ListingReview from './ListingReview';

export interface StepProps {
  isValid: boolean;
  isFirst: boolean;
  isLast: boolean;
  handleBack: () => void;
  handleNext: () => void;
  handleCancel: () => void;
  handleJump?: (idx: number) => void;
  setValidFn: (valid: boolean) => void;
}

interface StepDefinition {
  label: string;
  Component: any;
  isValid: boolean;
}

const stepDefs: StepDefinition[] = [
  {
    label: 'Car Info',
    Component: ListingModelInfo,
    isValid: false,
  },
  {
    label: 'Features',
    Component: ListingFeatures,
    isValid: false,
  },
  {
    label: 'Price/Contact Info',
    Component: ListingCarInfo,
    isValid: false,
  },
  {
    label: 'Photos',
    Component: ListingPhotos,
    isValid: false,
  },
  {
    label: 'Review',
    Component: ListingReview,
    isValid: true,
  },
];

export const CreateListing = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [addListing] = useListingsAddMutation();
  const [updateListing] = useListingsUpdateMutation();
  const client = useApolloClient();

  const listing = useSelector((state: RootState) => newListingSelectors.listing(state));
  const user = useSelector((state: RootState) => cacheSelectors.user(state));

  const [activeStep, activeStepTo] = useState(0);
  const [steps, stepsTo] = useState<StepDefinition[]>(stepDefs);
  const [showCancelDialog, showCancelDialogTo] = useState(false);
  const [deleteContent, deleteContentTo] = useState('');

  const handleNext = () => {
    activeStepTo(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    activeStepTo(prevActiveStep => prevActiveStep - 1);
  };

  const handleJump = (idx: number) => {
    activeStepTo(idx);
  };

  const handleFinish = async () => {
    try {
      if (listing.id) {
        const listingUpdate: ListingUpdate_Input = { ...listing };
        const { data } = await updateListing({
          variables: { object: listingUpdate },
        });
      } else {
        const { id, ...listingAdd } = listing;
        listingAdd.userId = user?.id as string;
        const { data } = await addListing({
          variables: { object: listingAdd },
        });
      }
      dispatch(
        cacheActions.addSnack({
          message: `Listing ${listing.id ? 'Updated' : 'Created'}`,
          variant: 'success',
        }),
      );
      dispatch(newListingActions.init());
      await client.clearStore();
      navigate(appRoutes.sell);
    } catch (error: any) {
      console.log('error', error);
      dispatch(
        cacheActions.addSnack({
          message: error.message,
          variant: 'error',
        }),
      );
    }
  };

  //#region Cancel Edit
  const onCancelConfirm = () => {
    showCancelDialogTo(false);
    if (listing.id) dispatch(newListingActions.init());
    navigate(appRoutes.sell);
  };

  const onContinueEditing = () => {
    showCancelDialogTo(false);
  };

  const onCancelIntent = () => {
    if (listing.id) {
      deleteContentTo('If you cancel, you will lose any changes.');
      showCancelDialogTo(true);
    } else if (listing.heading) {
      deleteContentTo('If you cancel, your progress will be temporarily be saved.');
      showCancelDialogTo(true);
    } else {
      dispatch(newListingActions.init());
      navigate(appRoutes.sell);
    }
  };

  const setValid = (step: number) => (valid: boolean) => {
    const values = _.cloneDeep(steps);
    values[step].isValid = valid;
    stepsTo(values);
  };

  const getStepContent = (step: number) => {
    const Comp = steps[step].Component;
    return (
      <Comp
        isValid={steps[step].isValid}
        setValidFn={setValid(step)}
        isFirst={step === 0}
        isLast={step === steps.length - 1}
        handleBack={handleBack}
        handleCancel={onCancelIntent}
        handleNext={step === steps.length - 1 ? handleFinish : handleNext}
        handleJump={handleJump}
      ></Comp>
    );
  };

  return (
    <WsGridContainer>
      <ConfirmDialog
        title="Cancel Create New Listing?"
        content={deleteContent}
        open={showCancelDialog}
        ackLabel="Confirm Cancel"
        ackAction={onCancelConfirm}
        nackLabel="Continue Editing"
        nackAction={onContinueEditing}
      />
      <Grid container sx={{ width: '85%' }}>
        <Grid item xs={12}>
          <WsFlexbox
            centered={false}
            sx={{
              marginBottom: 2,
            }}
          >
            <Typography variant="h4" color="primary">
              Listing Editor
            </Typography>{' '}
          </WsFlexbox>
          <Stepper activeStep={activeStep}>
            {steps.map((x, index) => {
              const stepProps: { completed?: boolean } = {};
              const labelProps: {
                optional?: React.ReactNode;
              } = {};
              return (
                <Step key={x.label} {...stepProps}>
                  <StepLabel {...labelProps}>{x.label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
          {activeStep === stepDefs.length ? (
            <React.Fragment>Finished</React.Fragment>
          ) : (
            <WsGridContainer>
              <WsInfoFrame width="100%" title={steps[activeStep].label}>
                {getStepContent(activeStep)}
              </WsInfoFrame>
            </WsGridContainer>
          )}
        </Grid>
      </Grid>
    </WsGridContainer>
  );
};

export default CreateListing;
